import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import SelectInput from "../utils/selectinput";
import { Constants } from "../../../helpers/constants";

const DYNAMIC_FORM_CONSTANT = Constants.forms;

export default function BrochureSelectionPopup({
  isOpen,
  onClose,
  models = [],
  initialModel = null,
  initialBrochureType = "",
  onSelect,
  floatSelectedModel
}) {
  const [selectedModel, setSelectedModel] = useState(floatSelectedModel ? floatSelectedModel : initialModel);
  const [brochureType, setBrochureType] = useState(initialBrochureType);
  const [errors, setErrors] = useState({});

  useEffect(() => {
   setSelectedModel(floatSelectedModel ? floatSelectedModel : initialModel);

   if (initialModel) {
     const hasModel = !!initialModel?.brochure;
     const hasAccessory = !!initialModel?.accessorybrochure;

     if (initialBrochureType) {
       setBrochureType(initialBrochureType);
       setErrors({ model: "", brochureType: "" });
     } else if (hasModel && hasAccessory) {
       setBrochureType("model"); // default select model
       setErrors({ model: "", brochureType: "" });
     } else if (hasModel) {
       setBrochureType("model");
       setErrors({ model: "", brochureType: "" });
     } else if (hasAccessory) {
       setBrochureType("accessory");
       setErrors({ model: "", brochureType: "" });
     } else {
       setBrochureType("");
       setErrors({});
     }
   } else {
     setBrochureType("");
     setErrors({});
   }
  }, [initialModel, initialBrochureType, isOpen]);

  // Pre-select from brochureSelectionConfig (mirrors DynamicFormComponent pattern)
  useEffect(() => {
   if (!isOpen) return;
   if (!models || models.length === 0) return;
   const raw = localStorage.getItem('brochureSelectionConfig');
   if (!raw) return;
   let cancelled = false;
   const cfg = JSON.parse(raw);
   // small delay to ensure any parent state / DOM settled
   setTimeout(() => {
     if (cancelled) return;
     // Model preselect
     if (cfg.preSelectedModel || cfg.preSelectedModelId) {
       const found = models.find(m =>
         m.model_code === cfg.preSelectedModel ||
         m.model_name === cfg.preSelectedModel ||
         m.model_code === cfg.preSelectedModelId ||
         m._id === cfg.preSelectedModelId ||
         m.id === cfg.preSelectedModelId
       );
       if (found) {
         setSelectedModel(found);
         const hasModel = !!found?.brochure;
         const hasAcc = !!found?.accessorybrochure;
         if (cfg.defaultBrochureType) {
           setBrochureType(cfg.defaultBrochureType);
         } else {
           if (hasModel && hasAcc) setBrochureType('model');
           else if (hasModel) setBrochureType('model');
           else if (hasAcc) setBrochureType('accessory');
           else setBrochureType('');
         }
         setErrors({ model: '', brochureType: '' });
       }
     }
     // Explicit brochure type override even if no model found
     if (cfg.defaultBrochureType && !brochureType) {
       setBrochureType(cfg.defaultBrochureType);
     }
     localStorage.removeItem('brochureSelectionConfig');
   }, 80);
   return () => { cancelled = true; };
  }, [isOpen, models]);

   // Brochure type options based on selected model
  const getBrochureTypeOptions = () => {
   if (!selectedModel) return [];
   const hasModel = !!selectedModel?.brochure;
   const hasAccessory = !!selectedModel?.accessorybrochure;
   if (hasModel && hasAccessory) {
     return [
       { name: DYNAMIC_FORM_CONSTANT?.BROCHURE_OPTION1, value: "model" },
       { name: DYNAMIC_FORM_CONSTANT?.BROCHURE_OPTION2, value: "accessory" },
     ];
   }
   if (hasAccessory) {
     return [{ name: DYNAMIC_FORM_CONSTANT?.BROCHURE_OPTION2, value: "accessory" }];
   }
   if (hasModel) {
     return [{ name: DYNAMIC_FORM_CONSTANT?.BROCHURE_OPTION1, value: "model" }];
   }
   return [];
  };

  const handleModelChange = (e) => {
   let parsed = null;
   try { parsed = JSON.parse(e.target.value); } catch { parsed = null; }
   setSelectedModel(parsed);
   if (parsed) {
     const hasModel = !!parsed?.brochure;
     const hasAccessory = !!parsed?.accessorybrochure;
     if (hasModel && hasAccessory) {
       setBrochureType('model');
       setErrors(prev => ({ ...prev, model: '', brochureType: '' }));
     } else if (hasModel) {
       setBrochureType('model');
       setErrors(prev => ({ ...prev, model: '', brochureType: '' }));
     } else if (hasAccessory) {
       setBrochureType('accessory');
       setErrors(prev => ({ ...prev, model: '', brochureType: '' }));
     } else {
       setBrochureType('');
       setErrors(prev => ({ ...prev, model: '' }));
     }
   } else {
     setBrochureType('');
   }
   setErrors(prev => ({ ...prev, model: '' }));
  };

  const handleBrochureTypeChange = (e) => {
   let parsed = null;
   try { parsed = JSON.parse(e.target.value); } catch { parsed = null; }
   const val = parsed?.value || '';
   setBrochureType(val);
   setErrors(prev => ({ ...prev, brochureType: '' }));
  };

  const handleSubmit = (e) => {
   e.preventDefault();
   let newErrors = {};

   if (!selectedModel) {
     newErrors.model = DYNAMIC_FORM_CONSTANT?.SELECT_A_VALID_CHOICE;
   }
   if (!brochureType) {
     newErrors.brochureType = DYNAMIC_FORM_CONSTANT?.SELECT_A_VALID_CHOICE;
   }

   setErrors(newErrors);

   if (Object.keys(newErrors).length === 0) {
     onSelect({ model: selectedModel, brochureType });
     onClose();
   }
  };

  // Brochure items with selected flag (avoid auto-selection flags in SelectInput)
  const brochureItems = getBrochureTypeOptions().map(opt => ({ ...opt, selected: opt.value === brochureType }));

  return (
  <Modal
  isOpen={isOpen}
  onRequestClose={onClose}
  contentLabel="Select Brochure"
  ariaHideApp={false}
  style={{
    overlay: {backgroundColor: "rgba(0,0,0,0.4)",display: "flex",alignItems: "flex-start",justifyContent: "center",zIndex: 1000,paddingTop: "80px"},
    content: {position: "relative",width: "800px",maxWidth: "90%",height: "auto",maxHeight: "80vh",margin: "0",padding: "24px", overflow: "visible",inset: "unset"}}}>
   <div style={{display: "flex",justifyContent: "space-between",alignItems: "center",marginBottom: "30px"}}>
    <h2 style={{ margin: 0 }}>{DYNAMIC_FORM_CONSTANT?.DOWNLOAD_BROCHURE_WITHOUT_LEAD_GENERATION}</h2>
    <button
     type="button"
     onClick={onClose}
     style={{background: "transparent",border: "none",fontSize: "20px",fontWeight: "bold",cursor: "pointer",lineHeight: 1,color: "#000"}}
     aria-label="Close"
    >✕</button>
   </div>
   <form onSubmit={handleSubmit}>
     <div className="form-fields" style={{ display: "flex", gap: "16px", marginBottom: 24 }}>
       <div style={{ flex: 1 }}>
         <SelectInput
           key={`model-select-${selectedModel?.model_code || 'default'}`}
           id="modelSelect"
           label="Model"
           required
           items={models}
           // selectinput now expects JSON stringified option values & relies on selectedModelCode for marking selected
           selectedModelCode={selectedModel?.model_code}
           handleChange={handleModelChange}
           placeholder={DYNAMIC_FORM_CONSTANT?.SELECT_MODEL}
           nameKey="model_name"
           valueKey="model_code"
         />
           {errors.model && (
             <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
               {errors.model}
             </p>
           )}
       </div>
       <div style={{ flex: 1 }}>
         <SelectInput
           id="brochureTypeSelect"
           label="Brochure Type"
           required
           // Pass no isBoth/isModelBrochureOnly flags so our selected property drives selection
           items={brochureItems}
           handleChange={handleBrochureTypeChange}
           placeholder={DYNAMIC_FORM_CONSTANT?.BROCHURE_SELECT}
           disabled={!selectedModel}
           nameKey="name"
         />
         {errors.brochureType && (
           <p style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
             {errors.brochureType}
           </p>
         )}
       </div>
     </div>

     <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
       <button
         type="submit"
         // disabled={!selectedModel || !brochureType}
         style={{background: "#001151", color: "#fff", border: "none", borderRadius: 120, padding: "8px 16px", cursor: "pointer"}}
       >
         {DYNAMIC_FORM_CONSTANT?.SUBMIT}
       </button>
     </div>
   </form>
</Modal>
  );
}